import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from './AuthContext';
import { useTeam } from './TeamContext';
import { DISCOVERY_ITEMS, DiscoveryItem } from '@/config/discovery';

interface DiscoveryContextType {
    activeHints: DiscoveryItem[];
    markAsSeen: (hintId: string) => Promise<void>;
    loading: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    dismissCurrentHint: () => Promise<void>;
}

const DiscoveryContext = createContext<DiscoveryContextType | undefined>(undefined);

export function DiscoveryProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuthContext();
    const { currentTeam } = useTeam();

    const [seenHints, setSeenHints] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);

    // Fetch seen hints when user changes
    useEffect(() => {
        if (!user) {
            setSeenHints([]);
            setLoading(false);
            return;
        }

        const fetchSeenHints = async () => {
            try {
                const { data, error } = await supabase
                    .from('user_hints')
                    .select('hint_id')
                    .eq('user_id', user.id);

                if (error) {
                    console.error('Error fetching seen hints:', error);
                    return;
                }

                if (data) {
                    setSeenHints(data.map(row => row.hint_id));
                }
            } catch (err) {
                console.error('Unexpected error fetching hints:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchSeenHints();
    }, [user]);

    const [config, setConfig] = useState<{ discovery_toast_interval_hours: number } | null>(null);

    // Fetch config
    useEffect(() => {
        const fetchConfig = async () => {
            const { data } = await supabase
                .from('app_configuration' as any)
                .select('value')
                .eq('key', 'discovery_toast_interval_hours')
                .maybeSingle();

            if (data) {
                setConfig({ discovery_toast_interval_hours: Number((data as any).value) });
            } else {
                // Fallback default if row is missing from DB
                setConfig({ discovery_toast_interval_hours: 24 });
            }
        };
        fetchConfig();
    }, []);

    // Determine active hints based on config, team settings, and seen status
    const activeHints = useMemo(() => {
        if (!user || !currentTeam || loading) return [];

        return DISCOVERY_ITEMS.filter(item => {
            // 1. Check if already seen
            if (seenHints.includes(item.id)) return false;

            // 2. Check if required module is enabled
            if (item.requiredModule) {
                // Helper to check module status based on team properties
                let moduleEnabled = false;
                switch (item.requiredModule) {
                    case 'fyp': moduleEnabled = currentTeam.showFypModule ?? false; break;
                    case 'tasks': moduleEnabled = currentTeam.showTasksModule; break;
                    case 'completed': moduleEnabled = currentTeam.showCompletedModule; break;
                    case 'groceries': moduleEnabled = currentTeam.showGroceryModule; break;
                    case 'stock': moduleEnabled = currentTeam.showStockModule; break;
                }
                if (!moduleEnabled) return false;
            }

            return true;
        });
    }, [user, currentTeam, loading, seenHints]);

    // Open (toast) if there are active hints and not already open
    // We check the last shown timestamp here
    useEffect(() => {
        if (!loading && activeHints.length > 0 && config) {
            const lastShownStr = localStorage.getItem('last_discovery_toast_shown_at');
            const lastShown = lastShownStr ? new Date(lastShownStr).getTime() : 0;
            const now = Date.now();
            const intervalMs = (config.discovery_toast_interval_hours ?? 24) * 60 * 60 * 1000;

            if (now - lastShown > intervalMs) {
                // Small delay to not overwhelm on immediate load
                const timer = setTimeout(() => {
                    setIsOpen(true);
                    // Update the timestamp when we actually show it
                    localStorage.setItem('last_discovery_toast_shown_at', new Date().toISOString());
                }, 1500);
                return () => clearTimeout(timer);
            }
        }
    }, [loading, activeHints.length, config]);

    const markAsSeen = useCallback(async (hintId: string) => {
        if (!user) return;

        // Optimistic update
        setSeenHints(prev => [...prev, hintId]);

        try {
            const { error } = await supabase
                .from('user_hints')
                .insert({
                    user_id: user.id,
                    hint_id: hintId,
                    status: 'seen'
                });

            if (error) {
                // Revert on error? Or just log.
                // For hints, strict consistency isn't critical, but let's log.
                console.error('Error marking hint as seen:', error);
            }
        } catch (err) {
            console.error('Unexpected error marking hint:', err);
        }
    }, [user]);

    const dismissCurrentHint = useCallback(async () => {
        if (activeHints.length > 0) {
            await markAsSeen(activeHints[0].id);
        }
    }, [activeHints, markAsSeen]);

    return (
        <DiscoveryContext.Provider value={{
            activeHints,
            markAsSeen,
            loading,
            isOpen,
            setIsOpen,
            dismissCurrentHint
        }}>
            {children}
        </DiscoveryContext.Provider>
    );
}

export const useDiscovery = () => {
    const context = useContext(DiscoveryContext);
    if (context === undefined) {
        throw new Error('useDiscovery must be used within a DiscoveryProvider');
    }
    return context;
};
